import React, { FC } from 'react';
import classNames from 'classnames';
import { Loading } from '@clearcover/atlas';
import { useAppContext } from 'QuoteApp/services/providers';
import styles from './styles.module.css';
import { Link } from 'react-router-dom';
import { useAuth0 } from 'Auth';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import urlJoin from 'url-join';
import { useQaToggleFeature } from 'Shared/hooks';
import { PaymentPlan } from './PaymentPlan/PaymentPlan';
import useRateConfig, { PaymentConfigType } from '../hooks/useRateConfig';
import { FlexiblePaymentPlan } from './FlexiblePaymentPlan/FlexiblePaymentPlan';

export const Rate: FC = () => {
  const { user } = useAuth0();
  const { appId, rate, rateLoading, details } = useAppContext();

  const isInfoReady = !!rate && !!details;

  const [rateComparisonFeatureFlag] = useQaToggleFeature(
    'hackathon_2023_rate_comparison'
  );
  const { isRateRenderable, rateHeaders, getPaymentConfig } = useRateConfig({
    details,
    rate,
  });

  if (!isRateRenderable) {
    return null;
  }

  const monthlyConfig = isInfoReady ? getPaymentConfig('monthly') : null;
  const inFullConfig = isInfoReady ? getPaymentConfig('in_full') : null;
  const fullPayConfig = isInfoReady ? getPaymentConfig('full_pay') : null;
  const weeklyConfig = isInfoReady ? getPaymentConfig('weekly') : null;
  const biWeeklyConfig = isInfoReady ? getPaymentConfig('bi_weekly') : null;
  const semiMonthlyConfig = isInfoReady
    ? getPaymentConfig('semi_monthly')
    : null;

  const renderFlexiblePaymentPlan = (
    config: PaymentConfigType | undefined | null
  ) => {
    if (!config || config.plan !== 'flexible') {
      return null;
    }

    return <FlexiblePaymentPlan {...config} />;
  };

  return (
    <div className={styles.container} data-testid="footer-rate">
      {rate && (
        <div
          className={classNames(styles.paymentPlanWrapper, {
            [styles.loading]: rateLoading,
          })}
        >
          <div className={styles.paymentPlanTitle}>{rateHeaders.title}</div>

          <div className={styles.paymentAmountWrapper}>
            {monthlyConfig && monthlyConfig.plan === 'monthly' && (
              <PaymentPlan {...monthlyConfig} data-testid="rate-monthly" />
            )}
            {monthlyConfig &&
              monthlyConfig.plan === 'monthly' &&
              !inFullConfig && (
                <PaymentPlan {...monthlyConfig} isMonthlyPlanOnly />
              )}
            {inFullConfig && inFullConfig.plan === 'full' && (
              <PaymentPlan {...inFullConfig} data-testid="rate-pay-in-full" />
            )}

            {renderFlexiblePaymentPlan(monthlyConfig)}
            {renderFlexiblePaymentPlan(fullPayConfig)}
            {renderFlexiblePaymentPlan(weeklyConfig)}
            {renderFlexiblePaymentPlan(biWeeklyConfig)}
            {renderFlexiblePaymentPlan(semiMonthlyConfig)}
          </div>

          <div className={styles.paymentPlanSubtitle}>
            {rateHeaders.subtitle}
          </div>
        </div>
      )}

      {rate && rateLoading && (
        <div className={styles.loadingIcon}>
          <Loading size={16} />
        </div>
      )}
      {rateComparisonFeatureFlag &&
        (user!.isCarrierUser || user?.isSystemUser) && (
          <Link
            className={styles.rateComparison}
            to={urlJoin(QUOTE_APP_ROUTE, appId, '/rate_comparison')}
            data-testid="rate-comparison-link"
          >
            What Changed?
          </Link>
        )}
    </div>
  );
};
